import { useEffect, useState } from "react";
import { Check, X, Search, Link2, Unlink } from "lucide-react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { eur, fmtDate } from "../../lib/format";
import api from "../../lib/api";

/** Minimale vorm van een kasboekregel zoals /api/kasboek/entries hem levert
 *  (met de gekoppelde uitgave/factuurbetaling via de include). */
export interface KoppelEntry {
  id: string;
  date: string;
  description: string;
  amount: number;
  account?: { number: string; name: string };
  expense?: { id: string; description: string; amountEurInclVat: number } | null;
  payment?: { id: string; amountEur: number; invoice?: { id: string; number: string } | null } | null;
}

interface CashSuggestion {
  targetType: "EXPENSE" | "INVOICE";
  targetId: string;
  score: number;
  reasons: string[];
  expense?: { id: string; description: string; merchant?: string | null; date: string; amountEurInclVat: number } | null;
  invoice?: { id: string; number: string; contactName?: string | null; openEur: number } | null;
}

interface KoppelPanelProps {
  isOpen: boolean;
  onClose: () => void;
  entry: KoppelEntry | null;
  onDone: () => void;
}

/** Koppel-modal voor kasboekregels (spiegel van de bank MatchSuggestionPanel):
 *  bovenin de kasboekregel (datum + omschrijving + rubriek + bedrag), daarna
 *  suggesties en handmatig zoeken. Regel "Uit" (bedrag < 0) koppelt aan een
 *  uitgave (contant betaald), regel "In" (bedrag > 0) aan een factuur. */
export function KoppelPanel({ isOpen, onClose, entry, onDone }: KoppelPanelProps) {
  const [suggestions, setSuggestions] = useState<CashSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [manualQuery, setManualQuery] = useState("");
  const [manualResults, setManualResults] = useState<{ id: string; label: string }[]>([]);

  const isIn = entry ? Number(entry.amount) > 0 : false;

  useEffect(() => {
    if (!isOpen || !entry) return;
    setError("");
    setSuggestions([]);
    setManualQuery("");
    setManualResults([]);
    setLoading(true);
    api
      .get(`/api/kasboek/entries/${entry.id}/suggestions`)
      .then((res) => setSuggestions(res.data.suggestions ?? []))
      .catch(() => setError("Suggesties ophalen mislukt."))
      .finally(() => setLoading(false));
  }, [isOpen, entry]);

  const apply = async (targetType: "EXPENSE" | "INVOICE", targetId: string) => {
    if (!entry) return;
    setBusy(true);
    setError("");
    try {
      await api.post(`/api/kasboek/entries/${entry.id}/link`, { targetType, targetId });
      onClose();
      onDone();
    } catch (err: any) {
      setError(err?.response?.data?.error || "Koppelen mislukt.");
    } finally {
      setBusy(false);
    }
  };

  const unlink = async () => {
    if (!entry) return;
    setBusy(true);
    setError("");
    try {
      await api.post(`/api/kasboek/entries/${entry.id}/unlink`);
      onClose();
      onDone();
    } catch (err: any) {
      setError(err?.response?.data?.error || "Ontkoppelen mislukt.");
    } finally {
      setBusy(false);
    }
  };

  const searchManual = async () => {
    if (!manualQuery.trim() || !entry) return;
    try {
      if (isIn) {
        const { data } = await api.get("/api/invoices", { params: { q: manualQuery } });
        setManualResults(
          (data ?? [])
            .filter((i: any) => !i.isCredit && (i.status === "SENT" || i.status === "OVERDUE"))
            .slice(0, 10)
            .map((i: any) => ({
              id: i.id,
              label: `${i.number} · ${i.contact?.companyName ?? i.contact?.name ?? ""} · ${eur(Number(i.totalEur ?? i.total))}`,
            })),
        );
      } else {
        const { data } = await api.get("/api/expenses", { params: { q: manualQuery } });
        setManualResults(
          (data ?? [])
            .filter((e: any) => !e.bankTransactionId && !e.cashBookEntryId)
            .slice(0, 10)
            .map((e: any) => ({
              id: e.id,
              label: `${e.description} · ${e.merchant ?? e.contact?.name ?? ""} · ${eur(Number(e.amountEurInclVat))} · ${fmtDate(e.date)}`,
            })),
        );
      }
    } catch {
      setManualResults([]);
    }
  };

  const linkedLabel = entry?.expense
    ? `Uitgave: ${entry.expense.description} (${eur(Number(entry.expense.amountEurInclVat))})`
    : entry?.payment
      ? `Factuur ${entry.payment.invoice?.number ?? ""} — betaling ${eur(Number(entry.payment.amountEur))}`
      : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Kasboekregel koppelen"
      subtitle={
        entry
          ? `${fmtDate(entry.date)} · ${entry.account ? `${entry.account.number} ${entry.account.name}` : ""} · ${entry.description} · ${eur(Number(entry.amount))}`
          : ""
      }
      size="md"
    >
      <div className="space-y-4">
        {entry && !isIn && (
          <p className="text-xs text-slate-500">
            Deze regel is een <strong>Uit</strong>-betaling: koppel een uitgave die contant is betaald. De uitgave geldt
            daarna als <strong>betaald</strong> en verdwijnt uit de openstaande crediteuren op de balans.
          </p>
        )}
        {entry && isIn && (
          <p className="text-xs text-slate-500">
            Deze regel is een <strong>In</strong>-betaling: koppel een factuur die contant is voldaan. De factuur wordt
            direct als (deels) betaald geregistreerd (kas-betaling).
          </p>
        )}

        {linkedLabel && (
          <div className="flex items-center justify-between gap-3 bg-emerald-50 border border-emerald-200 rounded-xl px-3 py-2">
            <p className="text-sm text-emerald-700 font-semibold min-w-0 truncate">
              <Link2 size={14} className="inline mr-1" /> {linkedLabel}
            </p>
            <Button variant="outline" size="sm" onClick={unlink} loading={busy} className="!px-3 !py-1.5 shrink-0">
              <Unlink size={14} /> Ontkoppelen
            </Button>
          </div>
        )}

        {!linkedLabel && (
          <>
            {loading && <p className="text-sm text-slate-500">Suggesties berekenen…</p>}
            {!loading && suggestions.length === 0 && !error && (
              <p className="text-sm text-slate-500">Geen concrete suggesties gevonden. Zoek handmatig (onderaan).</p>
            )}
            <div className="space-y-2">
              {suggestions.map((s, i) => (
                <div key={`${s.targetType}-${s.targetId}-${i}`} className="border border-slate-200 rounded-xl p-3 flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800 text-sm">
                      {s.expense
                        ? `Uitgave: ${s.expense.description}${s.expense.merchant ? ` — ${s.expense.merchant}` : ""}`
                        : `Factuur ${s.invoice?.number ?? ""}${s.invoice?.contactName ? ` — ${s.invoice.contactName}` : ""}`}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {s.expense
                        ? `${eur(s.expense.amountEurInclVat)} · ${fmtDate(s.expense.date)}`
                        : `${eur(s.invoice?.openEur ?? 0)} open`}{" "}
                      · {s.reasons.join(" · ") || "Geen concrete redenen"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`text-sm font-bold ${s.score >= 85 ? "text-emerald-600" : s.score >= 40 ? "text-amber-600" : "text-slate-400"}`}>{s.score}</span>
                    <Button onClick={() => apply(s.targetType, s.targetId)} loading={busy} className="!px-3 !py-1.5">
                      <Check size={16} /> Koppel
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-slate-200 pt-4 space-y-3">
              <p className="text-sm font-semibold text-slate-700">Handmatig koppelen</p>
              <Input
                label={isIn ? "Zoekterm (factuurnummer/klant)" : "Zoekterm (omschrijving/leverancier)"}
                value={manualQuery}
                onChange={(e) => setManualQuery(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") searchManual();
                }}
              />
              <Button variant="ghost" onClick={searchManual}>
                <Search size={18} /> Zoeken
              </Button>
              {manualResults.length > 0 && (
                <ul className="divide-y divide-slate-100 border border-slate-200 rounded-xl max-h-48 overflow-y-auto">
                  {manualResults.map((r) => (
                    <li key={r.id}>
                      <button
                        type="button"
                        onClick={() => apply(isIn ? "INVOICE" : "EXPENSE", r.id)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-slate-50 flex items-center justify-between gap-2"
                      >
                        <span className="truncate">{r.label}</span>
                        <Check size={16} className="text-emerald-600 shrink-0" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </>
        )}

        {error && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-3 py-2 flex items-center gap-2">
            <X size={16} /> {error}
          </p>
        )}
      </div>
    </Modal>
  );
}